import {
  Flex,
  SelectField,
  type SelectFieldProps,
  Space,
  Tag,
  Text,
  type TextColor,
} from '@/components';
import { useTagOptions } from '@/features/tag/hooks';
import { tagScopeType, type tagType } from '@/features/tag/models';
import { getCurrentFiscalYear } from '@/utils';
import type { FieldValues } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { TagIcon } from './TagIcon';

type Props<T extends FieldValues> = {
  type: z.infer<typeof tagType>;
  isMultiple?: boolean;
  startYear?: number | 'current';
  endYear?: number | 'current';
  hint?: string;
} & Omit<SelectFieldProps<T>, 'options' | 'showSearch' | 'mode'>;

const InternalTagSelectField = <T extends FieldValues>({
  type,
  isMultiple = true,
  startYear,
  endYear,
  allowChangeReadonlyTags,
  ...props
}: Props<T> & { allowChangeReadonlyTags: boolean }) => {
  const { t } = useTranslation();
  const currentFiscalYear = getCurrentFiscalYear();
  const tagOptions = useTagOptions({
    params: {
      startYear: startYear === 'current' ? currentFiscalYear : startYear,
      endYear: endYear === 'current' ? currentFiscalYear : endYear,
    },
    type,
  });

  return (
    <SelectField
      {...props}
      options={tagOptions.data?.map((tagOption) => {
        const isDisable = !allowChangeReadonlyTags && !tagOption.isTaggable;
        const textColor: TextColor = isDisable ? 'quaternary' : 'normal';

        return {
          value: tagOption.value,
          scope: tagOption.scope,
          disabled: isDisable,
          labelText: tagOption.label,
          label: isMultiple ? (
            <Space>
              <TagIcon scope={tagOption.scope} />
              <Text text={tagOption.label} color={textColor} />
            </Space>
          ) : (
            <Flex gap="small">
              <TagIcon scope={tagOption.scope} />
              <Text
                text={tagOption.label}
                maxWidth="fit-content"
                color={textColor}
              />
            </Flex>
          ),
        };
      })}
      useNotInOption
      loading={tagOptions.isLoading}
      mode={isMultiple ? 'multiple' : undefined}
      showSearch={isMultiple}
      label={props.label ?? t('tag')}
      tagRender={(props) => {
        const tagOption = tagOptions.data?.find(
          (tag) => tag.value === props.value,
        );

        return (
          <Tag
            icon={
              <TagIcon scope={tagOption?.scope || tagScopeType.Values.local} />
            }
            closable
            onClose={props.onClose}
          >
            <Text text={tagOption?.label} />
          </Tag>
        );
      }}
      filterOption={(input, options) =>
        options && typeof options.labelText === 'string'
          ? options.labelText.includes(input)
          : true
      }
      // do not allowClear on when !allowChangeReadonlyTags, it will clear readonly tags together unexpectedly
      allowClear={allowChangeReadonlyTags}
    />
  );
};

/**
 * TagSelectField: Used for tag search filter etc, it can add or delete taggable and viewable tags
 */
export const TagSelectField = <T extends FieldValues>(props: Props<T>) => (
  <InternalTagSelectField {...props} allowChangeReadonlyTags={true} />
);

/**
 * TagSelectFieldForEdit: Used for editing tags, it can add or delete only taggable tags
 * viewable(and not taggable) tags are visible, but disabled
 */
export const TagSelectFieldForEdit = <T extends FieldValues>(
  props: Props<T>,
) => <InternalTagSelectField {...props} allowChangeReadonlyTags={false} />;
